// Which Vee bumper the start page shows (docs/BUMPERS.md). Pure: no DOM, no storage. The bumper
// chunk, Settings, the tests and Ep3 (a copy, pinned by test vectors) all pick with it.
//
// A list entry has the manifest's fields (public/bumpers/manifest.json): id, weight, season
// (a key of SEASONS, or null), dates ({ from, until } New York days, until not included, or null),
// bg ("any" | "dark" | "light") and, for moving art, animated + still.
import { SEASONS, seasonOpen, nyDay } from "./seasons.js";

export const MODES = ["visit", "day", "pinned", "off"];
// How often a visit picks from the open season's set rather than the everyday set. A fixed share,
// not weight math, so new everyday art never waters the season down.
export const SEASON_SHARE = 0.7;

// "YYYY-MM-DD" in New York for an instant.
export function nyDateKey(now) {
	const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(now));
	const v = (t) => p.find((x) => x.type === t).value;
	return v("year") + "-" + v("month") + "-" + v("day");
}

// Whether an entry may show at `now` on a `look` ("dark" | "light"). `lite` (Lite mode, reduced
// motion or animations off) needs a still for moving art.
export function showable(b, now, look = "dark", lite = false) {
	if (!b || !(b.weight > 0)) return false;
	if (b.season && !seasonOpen(b.season, now)) return false;
	if (b.dates && !(now >= nyDay(b.dates.from) && now < nyDay(b.dates.until))) return false;
	if (b.bg && b.bg !== "any" && b.bg !== look) return false;
	if (lite && b.animated && !b.still) return false;
	return true;
}

export const eligible = (list, now, look, lite) => (list || []).filter((b) => showable(b, now, look, lite));

// FNV-1a, then mulberry32: the same numbers for the same day and list everywhere.
function seeded(str) {
	let h = 0x811c9dc5;
	for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
	let a = h >>> 0;
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function weighted(pool, r) {
	let total = 0;
	for (const b of pool) total += b.weight;
	let x = r() * total;
	for (const b of pool) { x -= b.weight; if (x < 0) return b; }
	return pool[pool.length - 1];
}

// The pool a draw comes from: a special day's art only, else the open season's set most of the
// time, else the everyday set.
function draw(list, r, avoid) {
	if (!list.length) return null;
	let pool = list.filter((b) => b.dates);
	if (!pool.length) {
		const season = list.filter((b) => b.season), everyday = list.filter((b) => !b.season);
		pool = season.length && (!everyday.length || r() < SEASON_SHARE) ? season : everyday;
	}
	if (avoid && pool.length > 1) pool = pool.filter((b) => b.id !== avoid);
	return weighted(pool, r);
}

// One a day: the same bumper all day in New York, for this list.
export function dayPick(list, now, look = "dark", lite = false) {
	const el = eligible(list, now, look, lite);
	return draw(el, seeded(nyDateKey(now) + ":" + el.map((b) => b.id).join(",")), null);
}

// opts: mode (MODES), now, last (the id shown last time, never repeated on "visit" while there is
// another), pin (the pinned id), look, lite, rnd.
export function pick(list, { mode = "visit", now = Date.now(), last = "", pin = "", look = "dark", lite = false, rnd = Math.random } = {}) {
	if (mode === "off") return null;
	if (mode === "pinned") {
		// A pin shows only when it could show anyway: a winter one waits for winter (Settings says
		// so, see pinWaiting) and a retired one is gone. Until then, a normal visit pick.
		const p = (list || []).find((b) => b.id === pin);
		if (showable(p, now, look, lite)) return p;
	}
	if (mode === "day") return dayPick(list, now, look, lite);
	return draw(eligible(list, now, look, lite), rnd, last);
}

// Why a pinned bumper isn't showing at `now`: { season, label, from, over } while its season is
// closed, { day, from, over } while its special day hasn't come or has gone. null when the pin can
// show, or when it's missing or retired (nothing to wait for).
export function pinWaiting(list, pin, now = Date.now()) {
	const p = pin && (list || []).find((b) => b.id === pin);
	if (!p || !(p.weight > 0)) return null;
	const s = p.season && SEASONS[p.season];
	if (s && !seasonOpen(p.season, now)) return { season: p.season, label: s.label, from: s.from, over: now >= s.until };
	if (p.dates) {
		const from = nyDay(p.dates.from), until = nyDay(p.dates.until);
		if (!(now >= from && now < until)) return { day: p.dates.from, from, over: now >= until };
	}
	return null;
}

// --- Packs (docs/BUMPERS.md, "Packs"): new bumpers published as content, without a release ----
//
// A pack row (from the index) is { id, hash, kind, from, until, weight, n: { dark, light },
// w: { dark, light }, added }: from/until are New York days or "" (no window), w is the sum of
// entry weights that can show on each look. A pack entry, once loaded, looks like a core one plus
// { pack, packTitle, kind, window, months, added } and a global id "<pack-id>/<slug>".
// The core table joins the draw as three stand-in packs (everyday, season, day), so a core
// bumper and a pack bumper of the same weight have the same odds.

export const FRESH_DAYS = 14; // a new pack shows twice as often for its first two weeks
export const DATED_BOOST = 3; // an entry inside its own dates shows more often within its pack
const DAY_MS = 864e5;

// A { from, until } New York window (until not included); null means always.
export function windowOpen(w, now) {
	if (!w) return true;
	return now >= nyDay(w.from) && now < nyDay(w.until);
}

// The New York month (1-12) at `now`.
export const nyMonth = (now) => Number(nyDateKey(now).slice(5, 7));

// Whether a loaded entry may show: the core rules plus its pack's window and its months.
export function entryShowable(e, now, look = "dark", lite = false) {
	if (!showable(e, now, look, lite)) return false;
	if (e.window && !windowOpen(e.window, now)) return false;
	if (e.months && !e.months.includes(nyMonth(now))) return false;
	return true;
}

// Which pool a pack kind draws in: a special day wins, a season gets SEASON_SHARE, the rest share.
export const poolOf = (kind) => (kind === "day" ? "day" : kind === "season" ? "season" : "everyday");

const packWindow = (row) => (row.from && row.until ? { from: row.from, until: row.until } : null);
export const packFresh = (row, now) => (row.added && now >= nyDay(row.added) && now < nyDay(row.added) + FRESH_DAYS * DAY_MS ? 2 : 1);

// The core table as stand-in packs for this moment: only groups with something showable.
export function corePacks(list, now, look, lite) {
	const groups = { day: [], season: [], everyday: [] };
	for (const b of eligible(list, now, look, lite)) groups[b.dates ? "day" : b.season ? "season" : "everyday"].push(b);
	return Object.entries(groups).filter(([, es]) => es.length).map(([kind, es]) => ({ id: "", kind, core: true, entries: es, score: es.reduce((s, b) => s + b.weight, 0) }));
}

// Pack rows that are open now and have something for this look, scored for the first stage:
// pack weight (10 = normal) x the sum of its entry weights on this look x freshness. The second
// stage then draws by entry weight inside the pack, so every entry's odds come out as
// entry weight x pack weight x freshness, whatever the pack sizes.
export function openPacks(rows, now, look) {
	const out = [];
	for (const r of rows || []) {
		const sum = r.w && r.w[look];
		if (!(sum > 0) || !(r.weight > 0) || !windowOpen(packWindow(r), now)) continue;
		out.push({ ...r, score: (r.weight / 10) * sum * packFresh(r, now) });
	}
	return out;
}

// Stage one's pool, with the same meaning as the core draw: any open special day wins, else the
// season's share, else everyday + fun + community.
export function choosePool(cands, r) {
	const by = { day: [], season: [], everyday: [] };
	for (const c of cands) by[poolOf(c.kind)].push(c);
	if (by.day.length) return by.day;
	return by.season.length && (!by.everyday.length || r() < SEASON_SHARE) ? by.season : by.everyday;
}

function weightedBy(list, r, w) {
	let total = 0;
	for (const x of list) total += w(x);
	let v = r() * total;
	for (const x of list) { v -= w(x); if (v < 0) return x; }
	return list[list.length - 1];
}

const entryWeight = (now) => (e) => e.weight * (e.dates && windowOpen(e.dates, now) ? DATED_BOOST : 1);

// Remembered ids: a ring of the last SEEN_MAX shown, each a 5-char base36 FNV-1a of the global id.
export const SEEN_MAX = 400;
export function seenKey(id) {
	let h = 0x811c9dc5;
	for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 0x01000193); }
	return ((h >>> 0) % 60466176).toString(36).padStart(5, "0"); // 36^5
}
export function seenHas(ring, id) {
	const k = seenKey(id);
	for (let i = 0; i < (ring || "").length; i += 5) if (ring.substr(i, 5) === k) return true;
	return false;
}
export function seenAdd(ring, id) {
	const k = seenKey(id);
	let out = "";
	for (let i = 0; i < (ring || "").length; i += 5) { const x = ring.substr(i, 5); if (x !== k && x.length === 5) out += x; }
	out += k;
	return out.length > SEEN_MAX * 5 ? out.slice(out.length - SEEN_MAX * 5) : out;
}
export function seenDrop(ring, ids) {
	const drop = new Set(ids.map(seenKey));
	let out = "";
	for (let i = 0; i < (ring || "").length; i += 5) { const x = ring.substr(i, 5); if (!drop.has(x)) out += x; }
	return out;
}

// The two-stage draw over core + packs. opts: rows (index pack rows), core (the baked table),
// useCore (the index's core flag), load(row) -> Promise<entries[] | null>, now, look, lite, rnd,
// seen (ring), avoid (id shown last). Resolves to { entry, reset } where reset lists ids whose
// seen marks should go (a pack whose every entry was seen), or null when nothing can show.
export async function drawPacks({ rows, core, useCore = true, load, now = Date.now(), look = "dark", lite = false, rnd = Math.random, seen = "", avoid = "" }) {
	const cands = [...(useCore ? corePacks(core, now, look, lite) : []), ...openPacks(rows, now, look)];
	if (!cands.length) return null;
	let pool = choosePool(cands, rnd), widened = false;
	let first = null, failed = false;
	const ok = (e) => entryShowable(e, now, look, lite);
	for (let attempt = 0; attempt < 3; attempt++) {
		if (!pool.length) {
			// Nothing in the chosen pool would load or show: the other pools, once.
			if (first || widened) break;
			widened = true;
			pool = cands.filter((x) => !x.done);
			if (!pool.length) break;
		}
		const c = weightedBy(pool, rnd, (x) => x.score);
		pool = pool.filter((x) => x !== c);
		c.done = true;
		const entries = c.core ? c.entries : await Promise.resolve(load(c)).catch(() => null);
		if (!entries) failed = true;
		const open = (entries || []).filter(ok);
		if (!open.length) { attempt--; continue; } // a pack that won't load or has nothing for now doesn't count
		if (!first) first = open;
		let fresh = open.filter((e) => !seenHas(seen, e.id));
		if (avoid && fresh.length > 1) fresh = fresh.filter((e) => e.id !== avoid);
		if (fresh.length) return { entry: weightedBy(fresh, rnd, entryWeight(now)), reset: [] };
	}
	// A pack that didn't load (offline, or the page navigating away mid-fetch) could have held
	// something unseen: no answer beats forgetting what was shown.
	if (!first || failed) return null;
	// Every entry tried was seen lately: forget the first pack's marks and draw from it again.
	let again = first;
	if (avoid && again.length > 1) again = again.filter((e) => e.id !== avoid);
	return { entry: weightedBy(again, rnd, entryWeight(now)), reset: first.map((e) => e.id) };
}

// One a day over core + packs: the same pick all day for everyone on the same index generation.
export function dayPickPacks({ rows, core, useCore = true, load, gen = 0, now = Date.now(), look = "dark", lite = false }) {
	return drawPacks({ rows, core, useCore, load, now, look, lite, rnd: seeded(nyDateKey(now) + ":" + gen) });
}

// Why a pinned pack bumper (its stored row) isn't showing at `now`, for Settings: { what, label,
// from, over } with what = "season" | "day" | "time" (its months). null when it can show.
export function rowWaiting(row, now = Date.now()) {
	if (!row || !(row.weight > 0)) return null;
	const s = row.season && SEASONS[row.season];
	if (s && !seasonOpen(row.season, now)) return { what: "season", label: s.label, from: s.from, over: now >= s.until };
	for (const [w, what] of [[row.window, row.kind === "day" ? "day" : "season"], [row.dates, "day"]]) {
		if (w && !windowOpen(w, now)) return { what, label: row.packTitle || "", from: nyDay(w.from), over: now >= nyDay(w.until) };
	}
	if (row.months && !row.months.includes(nyMonth(now))) {
		const [y, m] = nyDateKey(now).split("-").map(Number);
		let k = 1;
		while (k < 12 && !row.months.includes(((m - 1 + k) % 12) + 1)) k++;
		const nm = ((m - 1 + k) % 12) + 1, ny = y + Math.floor((m - 1 + k) / 12);
		return { what: "time", label: "", from: nyDay(ny + "-" + String(nm).padStart(2, "0") + "-01"), over: false };
	}
	return null;
}
